/**
 * Calendar Report API Helper Functions
 * 
 * This file provides helper functions to easily get API endpoints
 * for calendar report images (Therapy, Screening, Prescription, Assessment, Investigation)
 * based on the current portal and user role.
 * 
 * All APIs follow the pattern: /api/secure/{basePath}/emr-reports/{reportType}/images/{operation}
 * 
 * Usage:
 *   import { getTherapyReportApis, getPrescriptionApis, getInvestigationReportApis } from 'src/shared/utils/calendarReportApiHelper'
 *   
 *   const therapyApis = getTherapyReportApis()
 *   // Returns: { get: '/api/secure/...', save: '/api/secure/...', upload: '/api/secure/...' }
 */

import {
  getReportApis,
  REPORT_TYPES,
  PORTAL_TYPES,
  detectCurrentPortal,
  getSupportRole,
} from 'src/shared/config/calendarReportApiConfig'

/**
 * Gets therapy report API endpoints
 * @param {Object} options - Optional configuration
 * @param {string} options.portalType - Override portal detection
 * @param {string} options.supportRole - Specific support role
 * @returns {Object} Object with get, save, and upload API endpoints
 */
export function getTherapyReportApis(options = {}) {
  return getReportApis(REPORT_TYPES.THERAPY, options)
}

/**
 * Gets screening report API endpoints
 * @param {Object} options - Optional configuration
 * @param {string} options.portalType - Override portal detection
 * @param {string} options.supportRole - Specific support role
 * @returns {Object} Object with get, save, and upload API endpoints
 */
export function getScreeningReportApis(options = {}) {
  return getReportApis(REPORT_TYPES.SCREENING, options)
}

/**
 * Gets prescription API endpoints
 * @param {Object} options - Optional configuration
 * @param {string} options.portalType - Override portal detection
 * @param {string} options.supportRole - Specific support role
 * @returns {Object} Object with get, save, and upload API endpoints
 */
export function getPrescriptionApis(options = {}) {
  return getReportApis(REPORT_TYPES.PRESCRIPTION, options)
}

/**
 * Gets assessment report API endpoints
 * @param {Object} options - Optional configuration
 * @param {string} options.portalType - Override portal detection
 * @param {string} options.supportRole - Specific support role
 * @returns {Object} Object with get, save, and upload API endpoints
 */
export function getAssessmentReportApis(options = {}) {
  return getReportApis(REPORT_TYPES.ASSESSMENT, options)
}

/**
 * Gets investigation report API endpoints
 * @param {Object} options - Optional configuration
 * @param {string} options.portalType - Override portal detection
 * @param {string} options.supportRole - Specific support role
 * @returns {Object} Object with get, save, and upload API endpoints
 */
export function getInvestigationReportApis(options = {}) {
  return getReportApis(REPORT_TYPES.INVESTIGATION, options)
}

/**
 * Gets all report API endpoints for a specific event type
 * @param {string} eventType - Event type (e.g., 'therapy-session', 'screening', 'consultation')
 * @param {Object} options - Optional configuration
 * @returns {Object|null} Object with get, save, and upload API endpoints or null
 */
export function getReportApisByEventType(eventType, options = {}) {
  const eventTypeMap = {
    'therapy-session': REPORT_TYPES.THERAPY,
    'therapy': REPORT_TYPES.THERAPY,
    'therapy-report': REPORT_TYPES.THERAPY,
    'screening': REPORT_TYPES.SCREENING,
    'screening-report': REPORT_TYPES.SCREENING,
    'consultation': REPORT_TYPES.PRESCRIPTION,
    'prescription': REPORT_TYPES.PRESCRIPTION,
    'assessment': REPORT_TYPES.ASSESSMENT,
    'assessment-report': REPORT_TYPES.ASSESSMENT,
    'investigation': REPORT_TYPES.INVESTIGATION,
    'investigation-report': REPORT_TYPES.INVESTIGATION,
  }

  const reportType = eventTypeMap[eventType?.toLowerCase()]
  if (!reportType) {
    console.warn(`Unknown event type: ${eventType}`)
    return null
  }

  return getReportApis(reportType, options)
}

/**
 * Gets EMR report API data structure (for backward compatibility)
 * This matches the structure used in BookedSlotItemContent.jsx
 * @param {string} eventType - Event type
 * @param {Object} options - Optional configuration
 * @returns {Object} Object with getApiSignature, saveApiSignature, uploadImagesSignature, dialogTitle
 */
export function getEmrApiData(eventType, options = {}) {
  const apis = getReportApisByEventType(eventType, options)

  if (!apis) {
    return {
      getApiSignature: '',
      saveApiSignature: '',
      uploadImagesSignature: '',
      dialogTitle: 'Upload EMR Images',
    }
  }

  const eventTypeMap = {
    'therapy-session': 'Upload Therapy Report Images',
    'therapy': 'Upload Therapy Report Images',
    'therapy-report': 'Upload Therapy Report Images',
    'screening': 'Upload Screening Report Images',
    'screening-report': 'Upload Screening Report Images',
    'assessment': 'Upload Assessment Report Images',
    'assessment-report': 'Upload Assessment Report Images',
    'investigation': 'Upload Investigation Report Images',
    'investigation-report': 'Upload Investigation Report Images',
  }

  return {
    getApiSignature: apis.get || '',
    saveApiSignature: apis.save || '',
    uploadImagesSignature: apis.upload || '',
    dialogTitle: eventTypeMap[eventType?.toLowerCase()] || 'Upload EMR Images',
  }
}

/**
 * Gets prescription API signatures (for backward compatibility)
 * @param {Object} options - Optional configuration
 * @returns {Object} Object with prescriptionImagesGetApiSignature and prescriptionImagesSaveApiSignature
 */
export function getPrescriptionApiSignatures(options = {}) {
  const apis = getPrescriptionApis(options)
  const isSupportPortal = (options.portalType || detectCurrentPortal()) === PORTAL_TYPES.SUPPORT

  return {
    prescriptionImagesGetApiSignature: apis.get || (isSupportPortal ? '' : '/api/secure/doctor/prescription/images/get-by-eventId'),
    prescriptionImagesSaveApiSignature: apis.save || (isSupportPortal ? '' : '/api/secure/doctor/prescription/images/save'),
  }
}

/**
 * Validates that the current portal can access the given API endpoint
 * @param {string} apiEndpoint - API endpoint to validate
 * @returns {boolean} True if accessible, false otherwise
 */
export function canAccessApi(apiEndpoint) {
  if (!apiEndpoint) return false

  const portal = detectCurrentPortal()
  const cannotAccessCC = [
    PORTAL_TYPES.MENTOR,
    PORTAL_TYPES.CLINIC_ASSISTANT,
    PORTAL_TYPES.CONSUMER,
  ].includes(portal)

  if (cannotAccessCC && apiEndpoint.startsWith('/api/secure/cc/')) {
    return false
  }

  return true
}

// Export portal and report type constants for convenience
export { PORTAL_TYPES, REPORT_TYPES, detectCurrentPortal, getSupportRole }
